import { canUndoCard, undoLastCard } from "@highmarch/app";
import { CommandResult, Commands, LevelPhase } from "@highmarch/domain";
import { audio } from "../audio/audio";
import type { GestureHandlers } from "../input/gestures";
import type { HandHandlers } from "../ui/cardHand";
import type { HudHandlers } from "../ui/hud";
import type { LevelParts } from "./levelParts";

/** Degrees the HUD's turn buttons rotate the map by, per step. */
const RotateStepDegrees = 45;

/** The HUD's buttons: START / NEXT WAVE, speed, settings, turn, pause, the rally flag, the whole map, UNDO, wave chips. */
export function hudHandlers(p: LevelParts): HudHandlers {
  return {
    onStart: () => startPressed(p), onSpeed: (s) => speedTapped(p, s), onSettings: () => p.pause.openSettings(),
    onRotate: (steps) => { p.rig.onRotate(steps * RotateStepDegrees); p.raise("mapMoved"); }, onPause: () => p.pause.toggle(), onRally: () => p.rally.toggle(),
    onWholeMap: () => p.layout.toggleWholeMap(null), onUndo: () => undoCard(p),
    onEnemyChip: (archetype, boss, immunity) => p.enemyCard.show(p.sim.balance, { archetype, boss, live: null, owned: p.session.ownedTowers, immunity }),
  };
}

/** The card tray's buttons. */
export function handHandlers(p: LevelParts): HandHandlers {
  return {
    pick: (i) => p.taps.pick(i),
    skip: () => p.execute(Commands.skipOffer()),
    reroll: () => p.execute(Commands.reroll()),
    rotate: () => p.taps.rotateGhost(),
    place: () => p.taps.place(),
    back: () => p.execute(Commands.unpickCard()),
    undo: () => undoCard(p),
    chooseBranch: (slot) => p.execute(Commands.chooseBranch(slot)),
  };
}

/** The map's gestures: taps and long-presses act on the board, drags, pinches and twists move the view. */
export function gestureHandlers(p: LevelParts): GestureHandlers {
  const rig = p.rig;
  return {
    // The armed rally flag takes the next tap first, paused or not: planting it is a considered move worth a pause.
    tap: (x, y) => { if (p.rally.armed) p.rally.onTap(x, y); else if (!p.pause.tapResumes()) p.taps.onTap(x, y); },
    longPress: (x, y) => p.taps.onLongPress(x, y),
    dragBegin: (x, y) => { p.camFollow.take(); rig.dragBegin(x, y); p.raise("mapMoved"); },
    dragMove: (x, y) => rig.dragTo(x, y),
    dragEnd: (_x, _y, vx, vy) => { rig.dragEnd(); rig.fling(vx, vy); },
    pinch: (scale, cx, cy) => { p.camFollow.take(); rig.onPinch(scale, cx, cy); p.raise("mapMoved"); },
    rotate: (degrees) => { p.camFollow.take(); rig.onRotate(degrees); p.raise("mapMoved"); },
    rotateEnd: () => rig.rotateEnd(),
  };
}

/** START WAVES before the waves, NEXT WAVE between them, CALL during one (M30 #17: the next stage joins the wave). */
function startPressed(p: LevelParts): void {
  const sim = p.sim;
  if (sim.phase === LevelPhase.Ready) { if (p.execute(Commands.startWaves()) === CommandResult.Ok) p.raise("wavesStarted"); }
  else if (sim.phase === LevelPhase.BetweenWaves || sim.canCallNext()) { if (p.execute(Commands.callNextStage()) === CommandResult.Ok) p.raise("nextWaveCalled"); }
}

/** The HUD speed button: the player's own speed, which also ends the walkthrough's ×1 for this run. */
function speedTapped(p: LevelParts, speed: number): void {
  p.clock.speed = speed;
  p.guide.speedChosen();
  p.raise("speedChanged");
}

/** UNDO (D17): the game rebuilds the level around the session without the last card (app/undo.ts). */
function undoCard(p: LevelParts): void {
  if (p.onUndo === null || p.ended || p.disposed) return;
  const next = undoLastCard(p.session);
  if (next === null) return;
  audio.play("ui");
  p.onUndo(next);
}

/** Whether UNDO is offered now: before the waves, a card played and none in hand, and a game that can rebuild. */
export function undoable(p: LevelParts): boolean { return p.onUndo !== null && !p.ended && canUndoCard(p.session); }
